import React, { useState } from "react";
import { ExternalLink, ChevronDown, ChevronUp, Link as LinkIcon, CheckCircle2 } from "lucide-react";
import { BusinessBusiness } from "../types";
import { AudienceMode, AudienceEvidence } from "../utils/audienceEvidenceTypes";
import { getAudienceActionButtons, AudienceActionButton } from "../utils/audienceActionButtons";

interface AudienceActionButtonsBarProps {
  business: BusinessBusiness;
  audienceMode: AudienceMode;
  verifiedEvidences?: any[];
  className?: string;
  compact?: boolean;
}

export const AudienceActionButtonsBar: React.FC<AudienceActionButtonsBarProps> = ({
  business,
  audienceMode,
  verifiedEvidences,
  className = "",
  compact = false
}) => {
  const [showOverflow, setShowOverflow] = useState(false);

  const { primaryButtons, overflowButtons } = getAudienceActionButtons(
    business,
    audienceMode,
    verifiedEvidences
  );

  if (primaryButtons.length === 0 && overflowButtons.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-2 text-left font-sans ${className}`}>
      {/* Primary Action Buttons (Max 3) */}
      <div className="flex items-center flex-wrap gap-2">
        {primaryButtons.map((btn) => (
          <a
            key={btn.id}
            href={btn.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={btn.explanation}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all cursor-pointer shadow-3xs active:scale-95 ${
              btn.isOfficial
                ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs"
                : "bg-blue-600 hover:bg-blue-700 dark:bg-slate-800 dark:hover:bg-slate-700 text-white dark:text-slate-100 dark:border dark:border-slate-700 shadow-xs"
            }`}
          >
            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            <span>{btn.label}</span>
          </a>
        ))}

        {/* Toggle Overflow Buttons */}
        {overflowButtons.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowOverflow(!showOverflow);
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10.5px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-all cursor-pointer shadow-3xs"
            title="View additional verified pages and supporting sources"
          >
            <LinkIcon className="w-3 h-3 text-slate-500" />
            <span>
              {showOverflow ? "Hide extra pages" : `More useful pages (${overflowButtons.length})`}
            </span>
            {showOverflow ? (
              <ChevronUp className="w-3 h-3 text-slate-500" />
            ) : (
              <ChevronDown className="w-3 h-3 text-slate-500" />
            )}
          </button>
        )}
      </div>

      {/* Expandable Overflow / Supporting Sources */}
      {showOverflow && overflowButtons.length > 0 && (
        <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5 text-[10.5px] animate-fadeIn">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-slate-500 block pb-1 border-b border-slate-200/80">
            Supporting Sources & Useful Pages ({overflowButtons.length})
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
            {overflowButtons.map((btn) => (
              <a
                key={btn.id}
                href={btn.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={btn.explanation}
                className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40 text-slate-700 hover:text-indigo-700 transition font-medium truncate gap-1.5"
              >
                <span className="truncate font-semibold flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                  <span className="truncate">{btn.label}</span>
                </span>
                <ExternalLink className="w-3 h-3 text-slate-400 shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
